import {
  rules,
  useTabs,
  useToggle,
  XCol,
  XForm,
  XHorLine,
  XPlainButton,
  XRow,
  XText,
  XTextButton,
} from "@cdx/common";
import {useState} from "react";
import {useServerRequest} from "../../lib/request";
import {LoginForm} from "../../pages/login";
import {useAuthFlow} from "../auth/oauth-flow";

const signupFormRules = {
  email: [rules.isEmail, rules.isRequired],
};

export const SignupForm = ({welcomeAtPath}) => {
  const [values, setValues] = useState({email: ""});
  const [doneSubmit, setDoneSubmit] = useState(false);
  const [showEmailField, {toggle: toggleEmail}] = useToggle();
  const [doCreateAccount] = useServerRequest({
    path: "/services/create-account-in-open-deck",
  });

  const buttonProps = useAuthFlow({provider: "discord", intent: "signup"});

  const handleSubmit = ({email}) => {
    return doCreateAccount({email, welcomeAtPath}).then(
      () => {
        setDoneSubmit(true);
      },
      (err) => {
        return Promise.reject(
          err === "already_exists"
            ? {email: "You've already created an account. Click the link at the bottom to login."}
            : err
        );
      }
    );
  };
  if (doneSubmit)
    return (
      <XCol sp={1} px={3} py={4}>
        <XText preset="bold" color="white">
          Thanks!
        </XText>
        <XText color="purple200">A confirmation mail is on your way.</XText>
      </XCol>
    );
  else {
    return (
      <XCol>
        <XRow px={3} py={4} sp={2} align="center">
          <XPlainButton {...buttonProps} color="purpleOnDark">
            Register via Discord
          </XPlainButton>
          <XText preset="bold" size={2} color="purple300">
            or
          </XText>
          <XPlainButton color="purpleOnDark" active={showEmailField} onClick={toggleEmail}>
            Enter Email
          </XPlainButton>
        </XRow>
        {showEmailField && (
          <XCol
            as={XForm}
            rules={signupFormRules}
            values={values}
            onChange={setValues}
            sp={3}
            buttonLabel={"Let's go!"}
            onSubmit={handleSubmit}
            px={3}
            py={3}
            bg="white"
          >
            <XForm.Field
              type="email"
              label="Your email"
              autoFocus
              name="email"
              hint="You'll receive a mail with a confirmation link."
            />
          </XCol>
        )}
      </XCol>
    );
  }
};

const DiscordLogin = () => {
  const buttonProps = useAuthFlow({provider: "discord", intent: "login"});
  return (
    <XCol align="start" sp={3}>
      <XPlainButton {...buttonProps}>Login via Discord</XPlainButton>
      <XCol rounded="sm" px={1} py={0} bg="purple100">
        <XText size={1} color="purple600">
          This method also works if you've registered via email/password and your Discord email
          matches one of your Codecks account emails.
        </XText>
      </XCol>
    </XCol>
  );
};

export const LoginArea = () => {
  const {tabsEl, activeContent} = useTabs(
    [
      {key: "email", label: "Email/Password", content: <LoginForm />},
      {key: "discord", label: "Discord", content: <DiscordLogin />},
    ],
    "email"
  );
  return (
    <XCol sp={3}>
      {tabsEl}
      <XCol relative>{activeContent}</XCol>
    </XCol>
  );
};

export const JoinCommunityOverlayLayout = ({
  children,
  title = "Join this community!",
  explainer,
}) => (
  <XCol style={{width: "18rem"}}>
    <XCol sp={1} px={3} pt={3} pb={2}>
      <XText preset="bold" color="white" align="center">
        {title}
      </XText>
      {explainer && (
        <XText color="purple200" align="center" size={2}>
          {explainer}
        </XText>
      )}
    </XCol>
    <XHorLine color="purple500" />

    {children}
  </XCol>
);

const CreateAccountOverlay = ({title, explainer, onDiscordSignup, welcomeAtPath}) => {
  const [showLogin, {toggle}] = useToggle();

  return (
    <JoinCommunityOverlayLayout title={title} explainer={explainer}>
      {showLogin ? (
        <XCol px={3} py={3} sp={2} bg="white">
          <LoginArea />
        </XCol>
      ) : (
        <SignupForm onDiscordSignup={onDiscordSignup} welcomeAtPath={welcomeAtPath} />
      )}
      <XCol align="start" px={3} py={1} bg="purple800">
        <XTextButton onClick={toggle} color="white">
          {showLogin ? "Back" : "Do you have a Codecks account already?"}
        </XTextButton>
      </XCol>
    </JoinCommunityOverlayLayout>
  );
};

export default CreateAccountOverlay;
